import { TextFieldForm } from "@inputs/hook-form";
import { EditorFormV2 } from "@inputs/hook-form/EditorFormV2";
import { Box, Divider, Stack, Typography } from "@mui/material";
import type { Control } from "react-hook-form";
import type { ITrainingEditorSectionSetup, TTrainingSharedFormFieldValues } from "../types";
import type { ITrainingSharedFormSectionProps } from "./shared";

const getEditorProps = (section: ITrainingEditorSectionSetup) => ({
  id: section.id,
  height: section.height,
  init: {
    toolbar: "bold italic underline strikethrough numlist bullist link removeformat wordcount",
    // biome-ignore lint/style/useNamingConvention: TinyMCE expects this option name
    max_height: section.height,
    // biome-ignore lint/style/useNamingConvention: TinyMCE expects this option name
    autoresize_bottom_margin: 8,
  },
});

const renderEditorField = <TForm extends TTrainingSharedFormFieldValues>(
  control: Control<TForm>,
  fields: ITrainingSharedFormSectionProps<TForm>["fields"],
  section: ITrainingEditorSectionSetup
) => {
  const editorProps = getEditorProps(section);

  switch (section.name) {
    case "description":
      return <EditorFormV2 control={control} name={fields.description} {...editorProps} />;
    case "trainingModalities":
      return <EditorFormV2 control={control} name={fields.trainingModalities} {...editorProps} />;
    case "beneficiaries":
      return <EditorFormV2 control={control} name={fields.beneficiaries} {...editorProps} />;
    case "financingModes":
      return <EditorFormV2 control={control} name={fields.financingModes} {...editorProps} />;
    case "registrationModalities":
      return <EditorFormV2 control={control} name={fields.registrationModalities} {...editorProps} />;
  }
};

interface ITrainingSharedFormEditorSectionProps<TForm extends TTrainingSharedFormFieldValues>
  extends ITrainingSharedFormSectionProps<TForm> {
  control: Control<TForm>;
  section: ITrainingEditorSectionSetup;
}

export const TrainingSharedFormEditorSection = <TForm extends TTrainingSharedFormFieldValues>({
  control,
  fields,
  section,
}: ITrainingSharedFormEditorSectionProps<TForm>) => {
  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      <Typography variant="body1SemiBold">{section.title}</Typography>
      {renderEditorField(control, fields, section)}
      {section.name === "registrationModalities" && section.urlLabel && (
        <TextFieldForm
          control={control}
          name={fields.url}
          label={
            <Box
              sx={{
                pr: 1,
              }}
            >
              {section.urlLabel}
            </Box>
          }
          placeholder={section.urlLabel}
          fullWidth
          size="small"
        />
      )}
    </Stack>
  );
};

interface ITrainingSharedFormEditorSectionsProps<TForm extends TTrainingSharedFormFieldValues>
  extends ITrainingSharedFormSectionProps<TForm> {
  control: Control<TForm>;
  sections: readonly ITrainingEditorSectionSetup[];
}

export const TrainingSharedFormEditorSections = <TForm extends TTrainingSharedFormFieldValues>({
  control,
  fields,
  sections,
}: ITrainingSharedFormEditorSectionsProps<TForm>) => {
  return sections.map((section) => (
    <Stack
      key={section.id}
      sx={{
        gap: 2,
      }}
    >
      <TrainingSharedFormEditorSection control={control} fields={fields} section={section} />
      <Divider sx={(theme) => ({ backgroundColor: theme.palette.border.main })} />
    </Stack>
  ));
};
